import React, { useEffect } from 'react';
import PageLayout from '../../layout/page_layout';
import Typography from '../../components/typography';
import Container from '../../components/container';
import TextField from '../../components/textfield';
import Button from '../../components/button';
import './index.css'
import Box from '../../components/box';
import UploadPhotoButton from '../../components/upload_photo_button';
import useCurrentUserId from '../../hooks/useCurrentUserId';
import { useOnInputChange } from '../../hooks/useOnInputChange';
import { UserService } from '../../apilibrary';
import { downloadFile } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { Divider } from '@mui/material';
import StripeSettings from './stripe_settings';
import { trackUserProfileUpdate } from '../../utils/analytics';

interface SectionProps {
  title: string
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ title, children }) => {
  return (
    <Box mb={3}>
      <Typography fontSize={24} variant='subtitle2' mb={1}>{title}</Typography>
      {children}
    </Box>
  )
}

const EditProfilePage: React.FC = () => {
  const [photos, setPhotos] = React.useState<File[]>([])
  const [firstName, onChangeFirstName, setFirstName] = useOnInputChange('')
  const [lastName, onChangeLastName, setLastName] = useOnInputChange('')
  const [website, onChangeWebsite, setWebsite] = useOnInputChange('')
  const [bio, onChangeBio, setBio] = useOnInputChange('')
  const [disableSaveButton, setDisableSaveButton] = React.useState(false)
  const [error, setError] = React.useState<string>()
  const navigate = useNavigate()
  const userId = useCurrentUserId() as string

  useEffect(() => {
    const fetchStripeDetails = async (): Promise<void> => {
      const account = await UserService.getUserStripeAccount()
      console.log(account)
    }

    void fetchStripeDetails()
  }, [userId])

  useEffect(() => {
    const fetchCurrentUserProfile = async (): Promise<void> => {
      const profile = await UserService.getUserProfileByUserId(userId)
      if (profile) {
        setFirstName(profile.firstName ?? '')
        setLastName(profile.lastName ?? '')
        setWebsite(profile.website ?? '')
        setBio(profile.bio ?? '')
        if (profile.profilePicture) {
          const profilePictureUrl = await profile.profilePicture.getPublicUrl()
          const photoFile = await downloadFile(profilePictureUrl)
          setPhotos([photoFile])
        }
      }
    }

    void fetchCurrentUserProfile()
  }, [setBio, setFirstName, setLastName, setWebsite, userId])

  const getValidWebsiteUrl = (): string | undefined => {
    if (website.length === 0) {
      return undefined
    }
    return website.startsWith('http') ? website : 'https://' + website
  }

  const handleSaveUserProfile = async (): Promise<void> => {
    setError(undefined)
    if (firstName.length === 0 || lastName.length === 0) {
      setError('First name and last name are required')
      return
    }
    try {
      setDisableSaveButton(true)
      await UserService.setUserProfile(userId, {
        firstName,
        lastName,
        website: getValidWebsiteUrl(),
        bio,
        profilePicture: (photos.length > 0) ? photos[0] : undefined
      })
      await trackUserProfileUpdate(firstName, lastName, userId);
      navigate('/profile/' + userId)
    } catch (e) {
      console.log(e)
      if (e instanceof Error) {
        setError(e.message)
      } else {
        setError('Something went wrong')
      }
    } finally {
      setDisableSaveButton(false)
    }
  }

  return (
    <PageLayout addGutter authOnly>
      <Container maxWidth="sm">
        <Section title='Profile Picture'>
          <Box display={'block'} width={'300px'} mb={3}>
            <UploadPhotoButton
              showPreview
              previewStyles={{
                imageWidth: '100%',
                imageHeight: '100%'
              }}
              variant='outlined'
              images={photos}
              onUpload={setPhotos}
            >
              Set Profile Picture
            </UploadPhotoButton>
          </Box>
        </Section>
        <Section title='Basic Info'>
          <TextField
            label='First Name'
            value={firstName}
            onChange={onChangeFirstName}
            fullWidth
            gutterBottom
         />
          <TextField
            label='Last Name'
            value={lastName}
            onChange={onChangeLastName}
            gutterBottom
            fullWidth
         />
          <TextField
            label='Website'
            value={website}
            onChange={onChangeWebsite}
            fullWidth
            gutterBottom
          />
          <TextField
            label='Bio'
            value={bio}
            onChange={onChangeBio}
            fullWidth
            multiline
          />
        </Section>
        {(Boolean(error)) && <Typography color={'error'}>{error}</Typography>}
        <Button
          color='primary'
          sx={{ marginTop: '18px' }}
          disabled={disableSaveButton}
          onClick={handleSaveUserProfile}
          variant='contained'
        >
          Save Changes
        </Button>
        <Divider sx={{ mt: 4, mb: 4 }} />
        <Section title='Payment Preferences'>
          <StripeSettings />
        </Section>
      </Container>
    </PageLayout>
  )
}

export default EditProfilePage
